import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  FiAward,
  FiArrowUpRight,
  FiCheckCircle,
  FiChevronRight,
  FiMapPin,
} from "react-icons/fi";
import {
  FaqSection,
  LongFormSections,
} from "../Components/LongFormContent";
import { getRelatedServices, getServiceBySlug } from "../data/serviceData";
import { CalibrationPointIcon } from "../utils/calibrationIcons";

const ServiceCards = ({ items }) => (
  <ul className="service-detail-point-grid">
    {items.map((item, index) => (
      <li key={`${item}-${index}`}>
        <span>
          <CalibrationPointIcon label={item} />
        </span>
        <strong>{item}</strong>
      </li>
    ))}
  </ul>
);

const ForceAndHardnessTables = ({ sections }) => (
  <>
    {sections.map((section) => (
      <article key={section.title} className="service-detail-table-panel">
        <div className="service-detail-table-heading">
          <span>
            <CalibrationPointIcon label={section.title} />
          </span>
          <h2>{section.title}</h2>
        </div>

        <div className="service-detail-table-scroll">
          <table>
            <thead>
              <tr>
                <th>
                  Sr No
                </th>
                <th>
                  Parameter / Measured Quantity / Device Under Calibration
                </th>
                <th>
                  Range
                </th>
              </tr>
            </thead>
            <tbody>
              {section.entries.map((entry, index) => (
                <tr
                  key={`${section.title}-${entry.parameter}`}
                >
                  <td>{String(index + 1).padStart(2, "0")}</td>
                  <td>{entry.parameter}</td>
                  <td>{entry.range}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </article>
    ))}
  </>
);

const RelatedServices = ({ slug }) => {
  const relatedServices = getRelatedServices(slug);

  return (
    <section className="service-related-section">
      <div className="service-related-shell">
        <header>
          <span>Continue exploring</span>
          <h2>Related Calibration Services</h2>
        </header>
        <div className="service-related-grid">
          {relatedServices.map((service) => (
            <Link
              key={service.slug}
              to={`/services/${service.slug}`}
            >
              <span className="service-related-icon">
                <CalibrationPointIcon label={service.name} />
              </span>
              <strong>{service.name}</strong>
              <FiArrowUpRight aria-hidden="true" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const StandardServiceContent = ({ service }) => (
  <section className="service-standard-section">
    <div className="service-standard-shell">
      <header className="service-standard-intro">
        <span className="service-standard-icon">
          <CalibrationPointIcon label={service.name} />
        </span>
        <div>
          <span className="service-standard-kicker">Specialist calibration</span>
          <h2>Capability and accredited measurement range</h2>
          <p>{service.intro}</p>
        </div>
      </header>

      {service.items ? (
        <ServiceCards items={service.items} />
      ) : (
        <ForceAndHardnessTables sections={service.sections} />
      )}

      <div className="service-standard-action">
        <Link
          to="/contact"
        >
          Request a Quote
          <FiArrowUpRight aria-hidden="true" />
        </Link>
      </div>
    </div>
  </section>
);

const ServiceDetail = () => {
  const { serviceSlug } = useParams();
  const service = getServiceBySlug(serviceSlug);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const hasOnPageContent = Boolean(service.faqs && service.pageTitle);

  return (
    <main>
      <header
        className="recon-service-hero relative isolate flex min-h-[30rem] items-center overflow-hidden bg-cover bg-center px-4 py-16 text-white"
        style={{ backgroundImage: `url("${service.heroImage}")` }}
      >
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(100deg,rgba(6,25,35,0.96)_0%,rgba(7,43,56,0.88)_54%,rgba(7,43,56,0.5)_100%)]" />
        <div className="relative mx-auto w-full max-w-6xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-8 flex flex-wrap items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-white/65"
          >
            <Link to="/" className="transition hover:text-[#65d3df]">
              Home
            </Link>
            <FiChevronRight aria-hidden="true" />
            <Link to="/services" className="transition hover:text-[#65d3df]">
              Services
            </Link>
            <FiChevronRight aria-hidden="true" />
            <span className="text-[#65d3df]">{service.name}</span>
          </nav>

          <div className="recon-kicker recon-kicker--dark">
            <FiAward aria-hidden="true" />
            Accredited calibration service
          </div>
          <h1 className="mt-6 max-w-5xl text-3xl font-bold leading-[1.12] tracking-tight sm:text-4xl md:text-5xl">
            {service.pageTitle || service.name}
          </h1>
          {service.subtitle && (
            <h2 className="mt-5 max-w-4xl text-lg font-semibold leading-8 text-[#65d3df] md:text-2xl">
              {service.subtitle}
            </h2>
          )}
          <div className="mt-8 flex flex-wrap gap-3 text-sm font-semibold">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 backdrop-blur-sm">
              <FiCheckCircle className="text-[#65d3df]" aria-hidden="true" />
              Traceable Results
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 backdrop-blur-sm">
              <FiMapPin className="text-[#65d3df]" aria-hidden="true" />
              Pune & PAN India
            </span>
          </div>
        </div>
      </header>

      {hasOnPageContent ? (
        <>
          <section className="recon-service-intro">
            <div className="recon-service-intro-shell">
              <div className="recon-service-intro-label">
                <span className="recon-service-intro-icon">
                  <CalibrationPointIcon label={service.name} />
                </span>
                <div className="recon-kicker">Service overview</div>
                <p>
                  Accuracy. Traceability. Confidence.
                </p>
              </div>
              <div className="recon-service-intro-copy">
                {service.intro.map((paragraph, index) => (
                  <p
                    key={paragraph}
                    className={index === 0 ? "is-lead" : ""}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </section>
          <LongFormSections sections={service.sections} />
          <FaqSection faqs={service.faqs} />
        </>
      ) : (
        <StandardServiceContent service={service} />
      )}

      <RelatedServices slug={service.slug} />
    </main>
  );
};

export default ServiceDetail;
